
import { useState } from "react";
import { toast } from "sonner";
import { generatePatientFilePDF } from "@/utils/pdfGenerator";

export type FileExportFormat = "standard" | "detailed" | "compact" | "professional";

// Updated PatientFile interface to match database structure
export interface PatientFile {
  id?: string;
  file_number: string;
  last_name: string; 
  first_name: string;
  birth_name?: string;
  birth_date?: string;
  email?: string;
  phone_number?: string;
  gender?: string;
  marital_status?: string;
  height?: number;
  weight?: number;
  blood_type?: string;
  treating_doctor?: string;
  insurance_provider?: string;
  social_security_number?: string;
  smoker?: boolean;
  cigarettes_per_day?: number;
  allergies?: string;
  address?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  children_count?: number;
  professional_id?: string;
  created_at?: string;
  updated_at?: string;
  notes?: string; // Ajout du champ notes
}

export const usePatientFileExport = () => {
  const [isExporting, setIsExporting] = useState(false);

  // Function to export the patient file to PDF
  const exportToPDF = async (file: PatientFile, formatType: FileExportFormat = "professional") => {
    setIsExporting(true);
    
    try {
      console.log("Exporting patient file with data:", file);
      
      // Ensure all essential data is present
      if (!file.file_number) {
        console.warn("Missing file_number in patient data");
      }
      
      // Format address from components if they exist
      const formattedAddress = [
        file.address,
        file.postal_code,
        file.city,
        file.country
      ].filter(Boolean).join(", ");
      
      // Convert PatientFile to match the structure expected by the PDF generator
      const patientData = {
        last_name: file.last_name,
        first_name: file.first_name,
        birth_name: file.birth_name,
        birth_date: file.birth_date,
        file_number: file.file_number,
        address: formattedAddress || file.address,
        phone: file.phone_number,
        email: file.email,
        insurance_number: file.social_security_number,
        complementary_number: file.insurance_provider,
        gender: file.gender,
        marital_status: file.marital_status,
        children_count: file.children_count,
        height: file.height,
        weight: file.weight,
        blood_type: file.blood_type,
        treating_doctor: file.treating_doctor,
        smoker: file.smoker,
        cigarettes_per_day: file.cigarettes_per_day,
        notes: file.notes, // Ajout du champ notes dans l'exportation
        medical_info: {
          allergies: file.allergies || 'N/A'
        }
      };
      
      // Generate the PDF
      await generatePatientFilePDF(patientData, formatType);
      toast.success("Le fichier PDF a été généré avec succès");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Erreur lors de la génération du PDF");
    } finally {
      setIsExporting(false);
    }
  };

  // Function to export the patient file to CSV
  const exportToCSV = async (file: PatientFile) => {
    try {
      setIsExporting(true);
      
      // Prepare headers with all available fields
      const headers = [
        "Numéro de dossier", 
        "Nom", 
        "Prénom", 
        "Nom de naissance", 
        "Date de naissance", 
        "Email", 
        "Téléphone", 
        "Sexe",
        "Situation familiale",
        "Nombre d'enfants",
        "Taille (cm)",
        "Poids (kg)",
        "Groupe sanguin",
        "Médecin traitant",
        "N° Sécurité Sociale",
        "N° Mutuelle",
        "Fumeur",
        "Cigarettes par jour",
        "Allergies",
        "Adresse",
        "Code postal",
        "Ville",
        "Pays",
        "Notes" // Ajout du champ notes dans les en-têtes
      ];
      
      // Extract data corresponding to headers
      const data = [
        file.file_number || "N/A",
        file.last_name || "N/A",
        file.first_name || "N/A",
        file.birth_name || "N/A",
        file.birth_date ? new Date(file.birth_date).toLocaleDateString('fr-FR') : "N/A", 
        file.email || "N/A", 
        file.phone_number || "N/A",
        file.gender || "N/A",
        file.marital_status || "N/A",
        file.children_count?.toString() || "N/A",
        file.height?.toString() || "N/A",
        file.weight?.toString() || "N/A",
        file.blood_type || "N/A", 
        file.treating_doctor || "N/A", 
        file.social_security_number || "N/A",
        file.insurance_provider || "N/A", 
        file.smoker ? "Oui" : "Non",
        file.cigarettes_per_day?.toString() || "N/A",
        file.allergies || "N/A",
        file.address || "N/A",
        file.postal_code || "N/A",
        file.city || "N/A",
        file.country || "N/A",
        file.notes || "N/A" // Ajout du champ notes dans les données
      ];
      
      // Create CSV content
      const csvContent = [
        headers.join(","),
        data.join(",")
      ].join("\n");
      
      // Create blob and link for download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      link.setAttribute("href", url);
      link.setAttribute("download", `Dossier_${file.last_name || ""}_${file.first_name || ""}.csv`);
      link.style.visibility = "hidden";
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Le fichier CSV a été généré avec succès");
    } catch (error) {
      console.error("Error generating CSV:", error);
      toast.error("Erreur lors de la génération du CSV");
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportToPDF,
    exportToCSV,
    isExporting,
  };
};
